import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DragAndDrop, TextButton } from 'components'
import styled from 'styled-components'
import { DndError } from 'utils/constants'
import { resolveDndErrorMessage } from 'utils/generic'

import { DragDropFileRejection, DragDropEvent } from '../DragAndDrop/interfaces'

type DragDropProps = {
  accept?: string | Array<string>
  maxSize?: number
  multiple?: boolean
  defaultViewHeight?: string // 400px
  defaultViewWidth?: string // 400px
  onDrop: (acceptFiles: Array<File>, rejectedFiles: Array<DragDropFileRejection>, event: DragDropEvent) => void
}

const FileUploader = (props: DragDropProps) => {
  const { t } = useTranslation()
  const [error, setError] = useState<{ filename: string; message: string }>()

  const handleOnDrop = (
    acceptFiles: Array<File>,
    rejectedFiles: Array<DragDropFileRejection>,
    event: DragDropEvent,
  ) => {
    if (props.onDrop) {
      props.onDrop(acceptFiles, rejectedFiles, event)
    }

    handleDropError(rejectedFiles)
  }

  const handleDropError = (rejectedFiles: Array<DragDropFileRejection>) => {
    if (rejectedFiles.length > 0) {
      const tempError: { filename: string; message: string } = { filename: '', message: '' }

      rejectedFiles.forEach((f) => {
        f.errors.forEach((i) => {
          if (tempError.message === '') {
            tempError.filename = f.file.name

            switch (i.code) {
              case DndError['file-too-large']:
                tempError.message = props.maxSize
                  ? t('file_exceeds_limit_error', { size: props.maxSize / 1024 / 1024 })
                  : t('file_too_big_error')
                break
              default:
                tempError.message = resolveDndErrorMessage(i.code)
            }
          }
        })
      })

      setError(tempError)
      return
    }

    setError(undefined)
  }

  const getAcceptExtension = (accept: string | Array<string>) => {
    if (Array.isArray(accept)) {
      return accept.join(',')
    } else {
      return accept.replaceAll('image/', '').replaceAll(',', ', ')
    }
  }

  return (
    <DragAndDropContainer>
      <DragAndDrop {...props} onDrop={handleOnDrop}>
        <>
          <TextButton inline icon={['far', 'paperclip']}>
            {t('attach_file_lbl')}
          </TextButton>
          <DragAndDropColumn>
            ({t('file_max_size_lbl')} {props.maxSize ? `${props.maxSize / 1024 / 1024}MB` : '10MB'}
          </DragAndDropColumn>
          <DragAndDropColumn>
            {t('file_accepted_format_lbl')} {props.accept ? getAcceptExtension(props.accept) : 'jpeg, jpg, png'})
          </DragAndDropColumn>
        </>
      </DragAndDrop>
    </DragAndDropContainer>
  )
}

export default FileUploader

const DragAndDropContainer = styled.div`
  width: 100%;
  cursor: pointer;
`
const DragAndDropColumn = styled.div`
  margin-left: 0.5em;
  margin-right: 0.25em;
  display: inline-block;
  color: ${({ theme }) => theme.colors.gray6};
`
